import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

const TIPOS_RELACION = {
  yo_mismo: 'Yo mismo/a',
  tutor_legal: 'Tutor/a legal',
  padre: 'Padre',
  madre: 'Madre',
  responsable: 'Responsable',
  tutor_temporal: 'Tutor/a temporal',
  acompanante: 'Acompanante',
};

const GRAVEDAD_COLORS = {
  leve: '#22c55e',
  moderada: '#f59e0b',
  grave: '#ef4444',
};

const TIPO_INC_COLORS = {
  accidente: '#ef4444',
  enfermedad: '#f59e0b',
  medicacion: '#3b82f6',
  conducta: '#8b5cf6',
  otro: '#6b7280',
};

export default function AdminPersonas() {
  const { token } = useAuth();
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadPersonas = useCallback(async (searchTerm) => {
    setLoading(true);
    try {
      const r = await api.getAdminPersonas(token, searchTerm || undefined);
      setPersonas(r.personas || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadPersonas(); }, [loadPersonas]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => { loadPersonas(search); }, 400);
    return () => clearTimeout(timer);
  }, [search, loadPersonas]);

  async function openDetail(personaId) {
    setLoadingDetail(true);
    try {
      const r = await api.getAdminPersona(token, personaId);
      setDetail(r.persona);
    } catch (e) { console.error(e); alert('Error cargando persona'); }
    setLoadingDetail(false);
  }

  // Stats
  const menores = personas.filter(p => p.esMenor).length;
  const conSalud = personas.filter(p => p.alergias || p.medicacionActual || p.enfermedades).length;

  if (loading && personas.length === 0) return (
    <div className="loading-screen" style={{ minHeight: 'auto', padding: '3rem' }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  return (
    <div className="main-content page-enter">
      <div style={{ marginBottom: '1.5rem' }}>
        <div className="section-label">Administracion</div>
        <h1>Personas</h1>
        <p style={{ color: 'var(--mid)', fontStyle: 'italic', fontSize: '.95rem', marginTop: '.2rem' }}>
          Registro global de personas inscritas y sus datos
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <StatCard label="Total personas" value={personas.length} color="var(--sage)" />
        <StatCard label="Menores" value={menores} color="#3b82f6" />
        <StatCard label="Con info de salud" value={conSalud} color="#f59e0b" />
      </div>

      {/* Search */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          className="form-input"
          placeholder="Buscar por nombre o email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>

      {/* List */}
      {personas.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--mid)' }}>
          {search ? 'No se encontraron personas con ese criterio' : 'No hay personas registradas'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
          {personas.map(p => (
            <div
              key={p.id}
              className="card"
              style={{ padding: '.9rem 1rem', cursor: 'pointer', transition: 'border-color .15s' }}
              onClick={() => openDetail(p.id)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                    <span style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--white)' }}>
                      {p.nombre || `${p.firstName || ''} ${p.lastName1 || ''}`.trim()}
                    </span>
                    {p.edad != null && (
                      <span style={{ fontSize: '.75rem', color: 'var(--mid)' }}>({p.edad} a.)</span>
                    )}
                    {p.esMenor && (
                      <span className="badge" style={{
                        background: 'rgba(59,130,246,.15)', color: '#3b82f6',
                        border: '1px solid rgba(59,130,246,.3)', fontSize: '.6rem',
                      }}>Menor</span>
                    )}
                  </div>
                  <div style={{ fontSize: '.75rem', color: 'var(--mid)', marginTop: '.15rem' }}>
                    {p.createdAt && (() => {
                      const d = p.createdAt._seconds ? new Date(p.createdAt._seconds * 1000) : new Date(p.createdAt);
                      return isNaN(d) ? null : (
                        <span style={{ color: 'var(--sage)' }}>
                          ✍ Alta: {d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })} ·{' '}
                        </span>
                      );
                    })()}
                    {p.email && <span>{p.email} · </span>}
                    {p.relaciones?.length > 0 && (
                      <span>
                        {p.relaciones.map(r => TIPOS_RELACION[r.tipo] || r.tipo).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '.3rem', alignItems: 'center' }}>
                  {p.alergias && (
                    <span className="badge" style={{
                      background: 'rgba(245,158,11,.15)', color: '#f59e0b',
                      border: '1px solid rgba(245,158,11,.3)', fontSize: '.6rem',
                    }}>Alergias</span>
                  )}
                  {p.grupoSanguineo && (
                    <span className="badge" style={{
                      background: 'rgba(239,68,68,.15)', color: '#ef4444',
                      border: '1px solid rgba(239,68,68,.3)', fontSize: '.6rem', fontWeight: 700,
                    }}>{p.grupoSanguineo}</span>
                  )}
                  <span style={{ color: 'var(--mid)', fontSize: '.8rem' }}>›</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Loading detail overlay */}
      {loadingDetail && (
        <div className="modal-overlay">
          <div className="spinner spinner-lg" />
        </div>
      )}

      {/* Detail modal */}
      {detail && !loadingDetail && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDetail(null)}>
          <div className="modal modal-lg" style={{ maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <div>
                <div className="section-label">Ficha de persona</div>
                <h2 style={{ margin: 0 }}>
                  {detail.nombre || `${detail.firstName || ''} ${detail.lastName1 || ''}`}
                  {detail.edad != null && <span style={{ fontWeight: 400, fontSize: '.85rem', color: 'var(--mid)' }}> ({detail.edad} a.)</span>}
                </h2>
              </div>
              <button className="modal-close" onClick={() => setDetail(null)}>x</button>
            </div>

            {/* Datos personales */}
            <div style={{ marginBottom: '1rem' }}>
              <SectionLabel text="Datos personales" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '.5rem .8rem' }}>
                <Field label="Nombre" value={detail.firstName} />
                <Field label="Primer apellido" value={detail.lastName1} />
                <Field label="Segundo apellido" value={detail.lastName2} />
                <Field label="Fecha nacimiento" value={detail.fechaNacimiento} />
                <Field label="Sexo" value={detail.sex === 'M' ? 'Masculino' : detail.sex === 'F' ? 'Femenino' : detail.sex} />
                <Field label="Menor" value={detail.esMenor ? 'Si' : 'No'} />
              </div>
            </div>

            {/* Contacto */}
            {(detail.email || detail.phone) && (
              <div style={{ marginBottom: '1rem' }}>
                <SectionLabel text="Contacto" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '.5rem .8rem' }}>
                  <Field label="Email" value={detail.email} />
                  <Field label="Telefono" value={detail.phone ? `${detail.phoneExt || '+34'} ${detail.phone}` : null} />
                  <Field label="Pais" value={detail.country} />
                  <Field label="Municipio" value={detail.municipality} />
                  <Field label="Provincia" value={detail.province} />
                </div>
              </div>
            )}

            {/* Documentacion */}
            {detail.docNum && (
              <div style={{ marginBottom: '1rem' }}>
                <SectionLabel text="Documentacion" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '.5rem .8rem' }}>
                  <Field label="Tipo" value={detail.docType?.toUpperCase()} />
                  <Field label="Numero" value={detail.docNum} mono />
                  <Field label="Soporte" value={detail.docSupport} mono />
                  <Field label="Emision" value={detail.docEmission} />
                </div>
              </div>
            )}

            {/* Salud */}
            <div style={{ marginBottom: '1rem', padding: '.8rem', background: 'rgba(122,171,138,.07)', borderRadius: 'var(--radius)', border: '1px solid rgba(122,171,138,.15)' }}>
              <SectionLabel text="Salud" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.5rem .8rem' }}>
                <Field label="Alergias" value={detail.alergias} warn={!!detail.alergias} />
                <Field label="Enfermedades" value={detail.enfermedades} />
                <Field label="Medicacion actual" value={detail.medicacionActual} />
                <Field label="Grupo sanguineo" value={detail.grupoSanguineo} />
                <Field label="Tarjeta sanitaria" value={detail.tarjetaSanitaria} />
                <Field label="Autoriza medicacion" value={detail.autorizaMedicacion ? 'Si' : 'No'} />
              </div>
              <div style={{ marginTop: '.6rem', padding: '.5rem', background: 'rgba(255,255,255,.03)', borderRadius: 'var(--radius)' }}>
                <div style={{ fontSize: '.6rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--mid)', marginBottom: '.3rem' }}>
                  Contacto de emergencia
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '.3rem .8rem' }}>
                  <Field label="Nombre" value={detail.contactoEmergenciaNombre} />
                  <Field label="Relacion" value={detail.contactoEmergenciaRelacion} />
                  <Field label="Telefono" value={detail.contactoEmergenciaTelefono ? `${detail.contactoEmergenciaExt || '+34'} ${detail.contactoEmergenciaTelefono}` : null} />
                </div>
              </div>
            </div>

            {/* Relaciones / Tutores */}
            {detail.relaciones?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <SectionLabel text="Relaciones / Tutores" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
                  {detail.relaciones.map(r => (
                    <div key={r.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '.5rem .7rem', background: 'rgba(255,255,255,.04)',
                      borderRadius: 'var(--radius)', border: '1px solid rgba(122,171,138,.12)',
                    }}>
                      <div>
                        <span style={{ fontSize: '.82rem', color: 'var(--white)', fontWeight: 600 }}>
                          {r.tutor?.familyName || r.tutor?.email || r.tutorId}
                        </span>
                        <span style={{ fontSize: '.72rem', color: 'var(--mid)', marginLeft: '.5rem' }}>
                          {r.tutor?.email}
                        </span>
                      </div>
                      <span className="badge" style={{
                        background: 'rgba(122,171,138,.15)', color: 'var(--sage)',
                        border: '1px solid rgba(122,171,138,.3)', fontSize: '.65rem',
                      }}>
                        {TIPOS_RELACION[r.tipo] || r.tipo}
                        {r.esPrincipal && ' (principal)'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bookings */}
            {detail.bookings?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <SectionLabel text={`Inscripciones (${detail.bookings.length})`} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
                  {detail.bookings.map(b => (
                    <div key={b.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '.5rem .7rem', background: 'rgba(255,255,255,.04)',
                      borderRadius: 'var(--radius)', border: '1px solid rgba(122,171,138,.12)',
                    }}>
                      <div>
                        <span style={{ fontSize: '.82rem', color: 'var(--white)', fontWeight: 600 }}>{b.eventName}</span>
                        <span style={{ fontSize: '.72rem', color: 'var(--mid)', marginLeft: '.5rem' }}>{b.familyName}</span>
                      </div>
                      <span className={`badge ${b.status === 'confirmed' ? 'badge-green' : 'badge-amber'}`} style={{ fontSize: '.65rem' }}>
                        {b.status === 'confirmed' ? 'Confirmada' : b.status === 'pending_payment' ? 'Pago pendiente' : b.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Incidencias */}
            {detail.incidencias?.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <SectionLabel text={`Incidencias (${detail.incidencias.length})`} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
                  {detail.incidencias.map(inc => {
                    const gravColor = GRAVEDAD_COLORS[inc.gravedad] || '#6b7280';
                    const tipoColor = TIPO_INC_COLORS[inc.tipo] || '#6b7280';
                    return (
                      <div key={inc.id} style={{
                        padding: '.5rem .7rem', background: 'rgba(255,255,255,.04)',
                        borderRadius: 'var(--radius)', borderLeft: `3px solid ${gravColor}`,
                        border: '1px solid rgba(122,171,138,.12)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                            <span style={{ fontSize: '.82rem', color: 'var(--white)' }}>{inc.descripcion?.substring(0, 80)}{inc.descripcion?.length > 80 ? '...' : ''}</span>
                          </div>
                          <div style={{ display: 'flex', gap: '.3rem' }}>
                            <span className="badge" style={{ background: tipoColor + '22', color: tipoColor, border: `1px solid ${tipoColor}44`, fontSize: '.6rem' }}>
                              {inc.tipo}
                            </span>
                            <span className="badge" style={{ background: gravColor + '22', color: gravColor, border: `1px solid ${gravColor}44`, fontSize: '.6rem' }}>
                              {inc.gravedad}
                            </span>
                          </div>
                        </div>
                        <div style={{ fontSize: '.7rem', color: 'var(--mid)', marginTop: '.2rem' }}>{inc.eventoNombre}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setDetail(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,.04)', border: '1px solid rgba(122,171,138,.15)',
      borderRadius: 'var(--radius)', padding: '1rem', textAlign: 'center',
    }}>
      <div style={{ fontSize: '1.8rem', fontWeight: 800, color, fontFamily: "'Fraunces',serif" }}>{value}</div>
      <div style={{ fontSize: '.72rem', color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</div>
    </div>
  );
}

function SectionLabel({ text }) {
  return (
    <div style={{
      fontSize: '.65rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
      color: 'var(--sage)', marginBottom: '.4rem',
    }}>{text}</div>
  );
}

function Field({ label, value, warn, mono }) {
  return (
    <div>
      <div style={{ fontSize: '.6rem', fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--mid)', marginBottom: '.1rem' }}>
        {label}
      </div>
      <div style={{
        fontSize: '.82rem',
        color: value ? (warn ? '#f59e0b' : 'var(--white)') : 'var(--mid)',
        fontStyle: value ? 'normal' : 'italic',
        fontFamily: mono ? "'JetBrains Mono', monospace" : undefined,
      }}>
        {value || 'No indicado'}
      </div>
    </div>
  );
}
